import React from 'react';
import { Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';

export default function TakedownPolicy() {
    return (
        <div className="takedown-policy">
            <p className="text-muted mb-4">
                <em>Last Updated: March 2026</em>
            </p>

            <p>
                Sustainability Hub is committed to maintaining a safe,
                respectful, and lawful environment. This policy explains how to
                report content that violates our guidelines or applicable law,
                including copyright infringement, harmful content, and
                nonconsensual intimate imagery. We take all reports seriously
                and respond promptly.
            </p>

            <hr className="my-4" />

            {/* ── SECTION 1: General Content Reporting ── */}
            <h4 className="mt-4 mb-3 fw-bold text-primary">
                Reporting Harmful or Violating Content
            </h4>
            <p>
                If you encounter content on Sustainability Hub that violates our
                Community Guidelines or Acceptable Use Policy, including
                harassment, hate speech, spam, misinformation, or other harmful
                material, you can report it directly through the platform.
            </p>

            <h5 className="mt-4 mb-3 fw-bold">How to Report Content</h5>
            <p>
                Use the <strong>Report</strong> button on any post or reply to
                flag content for review. Your report is anonymous to other
                users. Reports are reviewed by community moderators and, where
                necessary, our platform team.
            </p>

            <h5 className="mt-4 mb-3 fw-bold">What We Review</h5>
            <ul className="ps-4">
                <li>Harassment, threats, or targeted abuse</li>
                <li>Hate speech or discrimination</li>
                <li>Spam or deceptive content</li>
                <li>Misinformation that may cause harm</li>
                <li>Any other violation of our Community Guidelines</li>
            </ul>

            <h5 className="mt-4 mb-3 fw-bold">Our Response Process</h5>
            <p>Upon receiving a content report, we will:</p>
            <ol className="ps-4">
                <li>
                    Review the report against our Community Guidelines within{' '}
                    <strong>72 hours</strong>
                </li>
                <li>
                    Take action if a violation is confirmed, which may include
                    removing the content, issuing a warning, or suspending the
                    account
                </li>
                <li>
                    Notify the content author that their post was removed and
                    the reason why
                </li>
                <li>
                    Provide the author with an opportunity to appeal (see
                    Appeals section below)
                </li>
            </ol>

            <h5 className="mt-4 mb-3 fw-bold">Appeals</h5>
            <p>
                If your content was removed and you believe this was in error,
                you may appeal by contacting us at{' '}
                <a href="mailto:support@sustainabilityhub.co">
                    support@sustainabilityhub.co
                </a>{' '}
                with the subject line{' '}
                <strong>&ldquo;Content Removal Appeal&rdquo;</strong>. Please
                include a description of the content and why you believe it does
                not violate our guidelines. We will review your appeal and
                respond within <strong>5 business days</strong>.
            </p>

            <hr className="my-4" />

            {/* ── SECTION 2: DMCA / Copyright ── */}
            <h4 className="mt-4 mb-3 fw-bold text-primary">
                Copyright Infringement (DMCA)
            </h4>
            <p>
                Sustainability Hub respects the intellectual property rights of
                others and expects our users to do the same. In accordance with
                the Digital Millennium Copyright Act (DMCA), we will respond
                promptly to claims of copyright infringement committed using our
                service.
            </p>

            <h5 className="mt-4 mb-3 fw-bold">Who Can Submit a DMCA Notice</h5>
            <p>DMCA takedown notices may be submitted by:</p>
            <ul className="ps-4">
                <li>The copyright owner</li>
                <li>
                    A person authorized to act on behalf of the copyright owner
                </li>
            </ul>
            <Alert variant="warning" className="mt-3">
                <strong>Warning:</strong> Please be aware that under 17 U.S.C. §
                512(f), you may be liable for damages (including costs and
                attorneys&apos; fees) if you knowingly materially misrepresent
                that material or activity is infringing.
            </Alert>

            <h5 className="mt-4 mb-3 fw-bold">Required Information</h5>
            <p>To be effective, a DMCA takedown notice must include:</p>
            <ol className="ps-4">
                <li>
                    A physical or electronic signature of the copyright owner or
                    authorized representative
                </li>
                <li>
                    Identification of the copyrighted work claimed to have been
                    infringed
                </li>
                <li>
                    Identification of the material that is claimed to be
                    infringing, including information sufficient to locate the
                    material (URL)
                </li>
                <li>
                    Your contact information (name, address, telephone number,
                    email)
                </li>
                <li>
                    A statement that you have a good faith belief that use of
                    the material is not authorized by the copyright owner, its
                    agent, or the law
                </li>
                <li>
                    A statement, under penalty of perjury, that the information
                    in the notification is accurate and that you are authorized
                    to act on behalf of the copyright owner
                </li>
            </ol>

            <h5 className="mt-4 mb-3 fw-bold">
                How to Submit a Takedown Request
            </h5>
            <p>You can submit a takedown request through our online form:</p>
            <p>
                <Link
                    to="/takedown"
                    className="btn btn-primary btn-primary-outline-hover"
                >
                    Submit Takedown Request
                </Link>
            </p>

            <h5 className="mt-4 mb-3 fw-bold">Our Response Process</h5>
            <p>Upon receiving a valid DMCA notice, we will:</p>
            <ol className="ps-4">
                <li>Review the notice for completeness within 24-72 hours</li>
                <li>
                    Remove or disable access to the allegedly infringing
                    material
                </li>
                <li>Notify the user who posted the content of the takedown</li>
                <li>
                    Provide information about the counter-notification process
                </li>
            </ol>

            <h5 className="mt-4 mb-3 fw-bold">Counter-Notification</h5>
            <p>
                If you believe your content was removed in error, you may submit
                a counter-notification containing:
            </p>
            <ul className="ps-4">
                <li>Your physical or electronic signature</li>
                <li>
                    Identification of the material that was removed and its
                    location before removal
                </li>
                <li>
                    A statement under penalty of perjury that you have a good
                    faith belief the material was removed by mistake
                </li>
                <li>Your name, address, and telephone number</li>
                <li>
                    Consent to jurisdiction of federal court and agreement to
                    accept service of process
                </li>
            </ul>

            <h5 className="mt-4 mb-3 fw-bold">Repeat Infringer Policy</h5>
            <p>
                In accordance with DMCA requirements, we maintain a policy to
                terminate, in appropriate circumstances, users who are repeat
                infringers of copyrighted material.
            </p>
        </div>
    );
}
